import { StaffDashboardCard } from "../components/StaffDashboardCard";
import { ChartAttendance } from "../components/ChartAttendance";
import { AdminCalRec } from "../layouts/AdminCalRec";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { useState, useEffect } from "react";
import "react-calendar/dist/Calendar.css";
import "../assets/css/dashboard.css";
import icon1 from "../assets/icon1.svg";
import icon2 from "../assets/icon2.svg";
import { useLocation } from "react-router-dom";
import useWindowDimensions from "../hooks/useWindowSize";
import DashboardPhotoCard from "../components/DashboardPhotoCard";
import { defaultUrl } from "../features/actions";
import { useSelector } from "react-redux";
import { getMediaThunk } from "../features/mediaSlice";
import { useAppDispatch } from "../app/hooks";
import { makeFetchRequest } from "../utilities/common";

// Global Token Configuration
let token = "";
const stringifiedToken = sessionStorage.getItem("token");
if (typeof stringifiedToken === "string") {
  token = stringifiedToken;
}
export const globalToken = token;

interface Uplis {
  count: number;
  text: string;
  icon?: React.ReactNode;
}

// Rewritten to match your exact standard sub-activity list layout
const UpLis = ({ count, text, icon }: Uplis) => {
  return (
    <div className="w-full flex items-center justify-between p-4 bg-[#F4F7FE]/60 hover:bg-[#F4F7FE] rounded-2xl transition-all duration-150 mb-3 last:mb-0">
      <div className="flex items-center gap-3">
        {icon && (
          <div className="h-9 w-9 bg-white text-[#012063] rounded-xl flex items-center justify-center text-sm shadow-xs border border-gray-100/50">
            {icon}
          </div>
        )}
        <span className="text-[14px] font-bold text-[#012063CC] tracking-tight">
          {text}
        </span>
      </div>
      <span className="text-lg font-black text-[#012063] font-mono">
        {count}
      </span>
    </div>
  );
};

export const Home = () => {
  const location = useLocation();
  const token = location.state?.token;

  const user = JSON.parse(sessionStorage.getItem("user") as string);
  const { images: photos } =
    useSelector((state: any): any => state.photos) || [];
  const { width: windowWidth } = useWindowDimensions();

  const [summaryData, setSummaryData] = useState({
    absent: 0,
    birthdays: 0,
    students: 0,
    classrooms: 0,
    loggedin: 0,
    loggedout: 0,
    messages: 0,
    "non-teachers": 0,
    parent: 0,
    photosandvideos: 0,
    teachers: 0,
  });

  let dispatch = useAppDispatch();
  useEffect(() => {
    fetchDashboardData();
    dispatch(getMediaThunk());
  }, []);

  const fetchDashboardData = async () => {
    try {
      const data = await makeFetchRequest(
        `${defaultUrl}/user/dashboard`,
        false,
        true,
        token,
      );
      setSummaryData(data);
    } catch (e: any) {}
  };

  const isMobileScreen = windowWidth < 440;

  return (
    <section className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 flex flex-col space-y-6 text-left">
      {/* Platform Header */}
      <div className="w-full">
        <TopAdminNav name="Jane Doe" title="Dashboard" />
        <p className="text-[#012063]/60 text-sm mt-1 font-medium">
          Here's what's happening at your school today
        </p>
      </div>

      {/* 3-Column Profile Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        {/* Card 1: Active Children */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <span className="text-2xl">👶</span>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
              ↑ 4.2% from last term
            </span>
          </div>
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-4">
            Active Children
          </p>
          <p className="text-4xl font-black text-[#012063] font-mono mt-1">
            {summaryData.students}
          </p>
        </div>

        {/* Card 2: Checked In Status */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <span className="text-2xl">✅</span>
            <span className="text-xs font-bold text-[#0096D1] bg-blue-50 px-2.5 py-1 rounded-full">
              78% attendance rate
            </span>
          </div>
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-4">
            Checked In Today
          </p>
          <p className="text-4xl font-black text-[#012063] font-mono mt-1">
            {summaryData.loggedin}
          </p>
        </div>

        {/* Card 3: Classrooms Info */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <span className="text-2xl">🏫</span>
            <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full">
              All active
            </span>
          </div>
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-4">
            Classrooms
          </p>
          <p className="text-4xl font-black text-[#012063] font-mono mt-1">
            {summaryData.classrooms}
          </p>
        </div>
      </div>

      {/* Main Split Interface Area */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start w-full">
        {/* Left/Middle Content Segment */}
        <div className="xl:col-span-2 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Chart Block */}
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs flex flex-col justify-between min-h-[340px]">
              <div>
                <h3 className="text-base font-black text-[#012063]">
                  Attendance Report
                </h3>
                <p className="text-xs font-medium text-slate-400 mt-0.5">
                  Weekly check-in overview
                </p>
              </div>
              <div className="flex items-center justify-center flex-1 py-4">
                <ChartAttendance />
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-50 text-center">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    In
                  </p>
                  <p className="text-sm font-bold text-[#0096D1] font-mono">
                    {summaryData.loggedin}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Out
                  </p>
                  <p className="text-sm font-bold text-amber-500 font-mono">
                    {summaryData.loggedout}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Absent
                  </p>
                  <p className="text-sm font-bold text-rose-500 font-mono">
                    {summaryData.absent}
                  </p>
                </div>
              </div>
            </div>

            {/* Activities Action Feed Component */}
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs min-h-[340px] flex flex-col">
              <div className="mb-4">
                <h3 className="text-base font-black text-[#012063]">
                  Activities
                </h3>
                <p className="text-xs font-medium text-slate-400 mt-0.5">
                  Recent school activity registry
                </p>
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <UpLis count={summaryData.messages} text="Messages" icon="💬" />
                <UpLis
                  count={summaryData.birthdays}
                  text="Birthdays"
                  icon="🎂"
                />
                <UpLis
                  count={summaryData.photosandvideos}
                  text="Photos & Videos"
                  icon="📷"
                />
              </div>
            </div>
          </div>

          {/* Recently Added Section Stream Layout */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
              <span className="h-2 w-2 rounded-full bg-[#0096D1]" />
              <h3 className="text-xs font-black tracking-wider text-slate-400 uppercase">
                Recently Uploaded Media
              </h3>
            </div>
            <div className="flex flex-wrap gap-4 items-center justify-start overflow-y-auto max-h-[220px] pr-1 register-scroll">
              {photos && photos.length > 0 ? (
                photos.map((item: any, index: number) => (
                  <div
                    key={index}
                    className="transition-transform duration-200 hover:scale-[1.02]"
                  >
                    <DashboardPhotoCard item={item} />
                  </div>
                ))
              ) : (
                <div className="w-full text-center py-8 text-slate-400 text-xs italic">
                  No recently updated multimedia uploads found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar Institutional Scheduler Container */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs hidden xl:block h-full">
          <div className="pb-3 border-b border-gray-100 mb-4">
            <h4 className="text-sm font-black text-[#012063]">
              Calendar Matrix
            </h4>
            <p className="text-[11px] font-medium text-slate-400">
              Manage schedules and routines
            </p>
          </div>
          <div className="w-full overflow-hidden rounded-2xl bg-slate-50/50 p-1">
            <AdminCalRec />
          </div>
        </div>
      </div>
    </section>
  );
};
